import type { ComponentType, ReactNode } from 'react';
import { Info } from 'lucide-react';
import { cn } from '@/lib/utils';

interface InfoNoteProps {
  /** Optional leading icon (defaults to an info circle). */
  icon?: ComponentType<{ className?: string }>;
  /** Body content — a string or rich nodes. */
  children: ReactNode;
  /** Visual tone. `muted` is the quiet default; `amber` draws more attention. */
  tone?: 'muted' | 'amber';
  className?: string;
}

/**
 * A small, quiet inline callout for helpful context (operating hours, what a
 * ticket type means, how a flow works). Decorative icon is hidden from screen
 * readers; the text carries the meaning.
 */
export function InfoNote({
  icon: Icon = Info,
  children,
  tone = 'muted',
  className,
}: InfoNoteProps) {
  return (
    <div
      className={cn(
        'flex items-start gap-2.5 rounded-xl border p-3 text-xs leading-relaxed',
        tone === 'amber'
          ? 'border-amber-300 bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-200'
          : 'border-border bg-muted/40 text-muted-foreground',
        className
      )}
    >
      <Icon className="mt-px h-4 w-4 shrink-0 opacity-80" aria-hidden="true" />
      <div>{children}</div>
    </div>
  );
}
